import {
  SET_TAGS,
  ADD_TAG_TO_EMPLOYER,
  ADD_TAG_TO_CONTACT,
  ADD_TAG_TO_ATTENDANCE,
  DELETE_TAG_FROM_EMPLOYER,
  DELETE_TAG_FROM_CONTACT,
  DELETE_TAG_TO_ATTENDANCE,
  SET_CONTACT_TAGS_FOR_CONTACT,
  SET_EMPLOYER_TAGS_FOR_EMPLOYER,
} from '../../mutation-types';
import ContactTagsClient from '@/modules/tags/infrastructure/api/ContactTagsClient';
import ContactTagService from '@/modules/tags/infrastructure/service/ContactTagService';

import AttendanceTagsClient from '@/modules/tags/infrastructure/api/AttendanceTagsClient';
import AttendanceTagService from '@/modules/tags/infrastructure/service/AttendanceTagService';
import http from '@/services/http';

const contactTagsApi = new ContactTagsClient(http);
const attendanceTagApi = new AttendanceTagsClient(http);
const contactTagService = new ContactTagService(contactTagsApi);
const attendanceTagService = new AttendanceTagService(attendanceTagApi);

const toArray = (v) => (Array.isArray(v) ? v : v ? Object.values(v) : []);

const formatTagGroupsForStore = (raw) => {
  const groups = toArray(raw);
  return groups
    .map((g) => {
      const items = toArray(g?.items).map((it) => ({
        tag_item_id: it?.tag_item_id ?? it?.id,
        tag_item_name: it?.tag_item_name ?? it?.name,
        tag_item_color: it?.tag_item_color ?? it?.color,
      }));
      const tag_id = g?.tag_id ?? g?.id;
      const tag_name = g?.tag_name ?? g?.name ?? g?.label;
      if (tag_id == null && !items.length) return null;
      return { tag_id, tag_name, items };
    })
    .filter(Boolean);
};

const parseStringGroups = (str) => {
  if (!str) return [];
  if (typeof str !== 'string') return toArray(str);
  const raw = str.trim();
  if (!raw || raw === '[]' || raw === 'undefined' || raw === 'null') return [];
  try {
    const parsed = JSON.parse(raw);
    return toArray(parsed);
  } catch {
    return [];
  }
};

const addItemToGroups = (groups, defs, tagItemId) => {
  const next = toArray(groups).map((g) => ({ ...g, items: toArray(g.items) }));
  // locate item and its group in definitions
  const defGroups = toArray(defs);
  let added = false;
  for (const g of defGroups) {
    const found = toArray(g.items).find(
      (it) => (it?.tag_item_id ?? it?.id) == tagItemId,
    );
    if (found) {
      const tag_id = g?.tag_id ?? g?.id;
      const tag_name = g?.tag_name ?? g?.name ?? g?.label;
      const tag_item_id = found?.tag_item_id ?? found?.id;
      const tag_item_name = found?.tag_item_name ?? found?.name;
      const tag_item_color = found?.tag_item_color ?? found?.color;
      const idx = next.findIndex((ng) => ng?.tag_id == tag_id);
      if (idx === -1) {
        next.push({
          tag_id,
          tag_name,
          items: [{ tag_item_id, tag_item_name, tag_item_color }],
        });
      } else {
        const set = new Set(
          toArray(next[idx].items).map((it) => it.tag_item_id),
        );
        if (!set.has(tag_item_id)) {
          next[idx].items.push({ tag_item_id, tag_item_name, tag_item_color });
        }
      }
      added = true;
      break;
    }
  }
  return { next, added };
};

const removeItemFromGroups = (groups, tagItemId) => {
  const next = toArray(groups).map((g) => ({ ...g, items: toArray(g.items) }));
  let changed = false;
  next.forEach((g) => {
    const before = g.items.length;
    g.items = g.items.filter((it) => it?.tag_item_id != tagItemId);
    if (g.items.length !== before) changed = true;
  });
  return {
    next: next.filter((g) => toArray(g.items).length > 0),
    changed,
  };
};

const actions = {
  async fetchTags({ commit }, params = {}) {
    try {
      const { data } = await contactTagService.fetchTags(params);
      commit(SET_TAGS, { data: data || [], tag_type: params.tag_type });
    } catch (err) {
      const message = err?.message || 'Não foi possível carregar as tags.';
      console.error(message);
    }
  },

  async addTagToContact(
    { commit, rootState },
    { contactId, agentId, tagItemId },
  ) {
    try {
      const { data } = await contactTagService.addTagToContact(
        contactId,
        agentId,
        tagItemId,
      );
      commit(ADD_TAG_TO_CONTACT, data || []);

      // Prefer API data; fallback to merge with existing job/contact tags
      let payloadArray = formatTagGroupsForStore(data || []);
      if (!payloadArray.length) {
        const defs = rootState?.tag?.tagsByType?.contact || [];
        // find a job for this contact to read current string
        const job = (rootState?.jobs?.jobList || []).find(
          (j) => j?.contact?.id === contactId,
        );
        const currentStr = job?.contact?.contact_tags || '[]';
        const currentGroups = parseStringGroups(currentStr);
        const { next, added } = addItemToGroups(currentGroups, defs, tagItemId);
        payloadArray = next;
      }
      const payloadString = JSON.stringify(payloadArray);
      commit(
        `jobs/${SET_CONTACT_TAGS_FOR_CONTACT}`,
        { contactId, contactTags: payloadString },
        { root: true },
      );
    } catch (err) {
      const message =
        err?.message || 'Não foi possível adicionar a tag ao contato.';
      console.error(message);
    }
  },

  async addTagToEmployer(
    { commit, rootState },
    { employerId, agentId, tagItemId },
  ) {
    try {
      const { data } = await contactTagService.addTagToEmployer(
        employerId,
        agentId,
        tagItemId,
      );
      commit(ADD_TAG_TO_EMPLOYER, data || []);

      let payloadArray = formatTagGroupsForStore(data || []);
      if (!payloadArray.length) {
        const defs = rootState?.tag?.tagsByType?.employer || [];
        const job = (rootState?.jobs?.jobList || []).find((j) => {
          const jc = j?.contact || {};
          const id =
            jc.employerId || jc.employer_id || jc?.employer?.employerId;
          return id === employerId;
        });
        const currentStr = job?.contact?.employer_tags || '[]';
        const currentGroups = parseStringGroups(currentStr);
        const { next, added } = addItemToGroups(currentGroups, defs, tagItemId);
        payloadArray = next;
      }
      const payloadString = JSON.stringify(payloadArray);
      commit(
        `jobs/${SET_EMPLOYER_TAGS_FOR_EMPLOYER}`,
        { employerId, employerTags: payloadString },
        { root: true },
      );
    } catch (err) {
      const message =
        err?.message || 'Não foi possível adicionar a tag ao empregador.';
      console.error(message);
    }
  },

  async addAttendanceTagToAgent({ commit }, { protocol, tagId, body }) {
    try {
      const data = await attendanceTagService.addAttendanceTagToAgent(
        protocol,
        tagId,
        body,
      );
      commit(ADD_TAG_TO_ATTENDANCE, { protocol, data: data || [] });
      if (Array.isArray(data) && data.length && data[0]?.items) {
        commit(
          `jobs/SET_ATTENDANCE_TAGS_JOB`,
          { protocol, attendanceTags: data, merge: false },
          { root: true },
        );
      }
    } catch (err) {
      const message =
        err?.message || 'Não foi possível adicionar a tag ao atendimento.';
      console.error(message);
    }
  },

  async deleteTagFromEmployer(
    { commit, rootState },
    { employerId, agentId, tagItemId },
  ) {
    try {
      const { data } = await contactTagService.deleteTagFromEmployer(
        employerId,
        agentId,
        tagItemId,
      );
      commit(DELETE_TAG_FROM_EMPLOYER, data || []);

      let payloadArray = formatTagGroupsForStore(data || []);
      if (!payloadArray.length) {
        const defs = rootState?.tag?.tagsByType?.employer || [];
        const job = (rootState?.jobs?.jobList || []).find((j) => {
          const jc = j?.contact || {};
          const id =
            jc.employerId || jc.employer_id || jc?.employer?.employerId;
          return id === employerId;
        });
        const currentStr = job?.contact?.employer_tags || '[]';
        const currentGroups = parseStringGroups(currentStr);
        const { next } = removeItemFromGroups(currentGroups, tagItemId);
        payloadArray = next;
      }
      const payloadString = JSON.stringify(payloadArray);
      commit(
        `jobs/${SET_EMPLOYER_TAGS_FOR_EMPLOYER}`,
        { employerId, employerTags: payloadString },
        { root: true },
      );
    } catch (err) {
      const message =
        err?.message || 'Não foi possível deletar a tag do empregador.';
      console.error(message);
    }
  },

  async deleteTagFromContact(
    { commit, rootState },
    { contactId, agentId, tagItemId },
  ) {
    try {
      const { data } = await contactTagService.deleteTagFromContact(
        contactId,
        agentId,
        tagItemId,
      );
      commit(DELETE_TAG_FROM_CONTACT, data || []);

      let payloadArray = formatTagGroupsForStore(data || []);
      if (!payloadArray.length) {
        const defs = rootState?.tag?.tagsByType?.contact || [];
        const job = (rootState?.jobs?.jobList || []).find(
          (j) => j?.contact?.id === contactId,
        );
        const currentStr = job?.contact?.contact_tags || '[]';
        const currentGroups = parseStringGroups(currentStr);
        const { next } = removeItemFromGroups(currentGroups, tagItemId);
        payloadArray = next;
      }
      const payloadString = JSON.stringify(payloadArray);
      commit(
        `jobs/${SET_CONTACT_TAGS_FOR_CONTACT}`,
        { contactId, contactTags: payloadString },
        { root: true },
      );
    } catch (err) {
      const message =
        err?.message || 'Não foi possível deletar a tag do contato.';
      console.error(message);
    }
  },

  async removeAttendanceTagToAgent({ commit }, { protocol, tagId, body }) {
    try {
      const data = await attendanceTagService.removeAttendanceTagToAgent(
        protocol,
        tagId,
        body,
      );
      commit(DELETE_TAG_TO_ATTENDANCE, { protocol, data: data || [] });
      if (Array.isArray(data) && data.length && data[0]?.items) {
        commit(
          `jobs/SET_ATTENDANCE_TAGS_JOB`,
          { protocol, attendanceTags: data, merge: false },
          { root: true },
        );
      }
    } catch (err) {
      const message =
        err?.message || 'Não foi possível remover a tag do atendimento.';
      console.error(message);
    }
  },
};

export default actions;
